import { environment } from "@environment";
import { useReducer, useState, type ChangeEvent, type FormEvent } from "react";
import {
  KEYS,
  PAGE_TITLE,
  PAGE_URL,
  SUBMISSION_DISABLED,
  VERSION_DATA,
} from "../constants";
import type { AcceptResponse } from "../types";
import {
  getBrowserName,
  getDeviceTypeByScreenSize,
  isEmailValid,
} from "../utils";

type FetchStatus = "idle" | "loading" | "success" | "error";
type FormData = { name: string; email: string; role: string };

const reducer = (
  current: FormData,
  update: Record<"name" | "email" | "role", string>,
) => ({
  ...current,
  ...update,
});

export const useForm = ({
  setResult,
}: {
  setResult: (result: AcceptResponse[]) => void;
}) => {
  const [status, setStatus] = useState<FetchStatus>("idle");
  const [touched, setTouched] = useState<boolean>(false);
  const [formData, setFormData] = useReducer(reducer, {
    name: "",
    email: "",
    role: "",
  });

  const setInputData = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const token = sessionStorage.getItem("accept-token-2krBd1i2VjStO00");
    setTouched(true);
    if (
      !formData.name ||
      !isEmailValid(formData.email) ||
      !token ||
      SUBMISSION_DISABLED
    )
      return;
    const additionalPayload = {
      pageUrl: PAGE_URL,
      pageTitle: PAGE_TITLE,
      device: getDeviceTypeByScreenSize(),
      browser: getBrowserName(),
      sessionTimestamp: window.sessionStartTimestamp,
      timestamp: new Date().toUTCString(),
    };

    setStatus("loading");
    const payload: Record<string, string> = {
      ...formData,
      ...additionalPayload,
    };
    KEYS.forEach((key) => (payload[key] = VERSION_DATA[key]));

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", `Bearer ${token}`);

    const options: RequestInit = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(payload),
      redirect: "follow",
    };

    try {
      const response = await fetch(environment.acceptButton, options);
      const result = await response.json();
      setResult(result);
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setResult([]);
    }
  };

  return { formData, touched, status, handleSubmit, setInputData };
};
